import { useEffect, useState } from "react";
import { X, Globe, Lock, Crown, User } from "lucide-react";
import { bandiApi, documentsApi } from "../../../services/ragApi";
import { useRag } from "../RagContext";
import "./DocumentVisibilityModal.css";

export default function DocumentVisibilityModal({ document: doc, onClose }) {
    const { currentBandoId, loadDocuments } = useRag();
    const [visibility, setVisibility] = useState("public");
    const [allowedIds, setAllowedIds] = useState(new Set());
    const [bandoMembers, setBandoMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!doc) return;
        let cancelled = false;
        setLoading(true);
        setError(null);

        Promise.all([
            documentsApi.getVisibility(doc.id),
            bandiApi.listMembers(doc.bandoId || currentBandoId),
        ])
            .then(([visResp, memResp]) => {
                if (cancelled) return;
                const visData = visResp?.data ?? {};
                setVisibility(visData.visibility || "public");
                const ids = new Set((visData.allowedUsers || []).map((u) => u.userId));
                setAllowedIds(ids);
                setBandoMembers(memResp?.data || []);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err.response?.data?.error || err.message || "Errore nel caricamento");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [doc, currentBandoId]);

    if (!doc) return null;

    const toggleUser = (userId) => {
        setAllowedIds((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return next;
        });
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const payload = {
                visibility,
                allowedUserIds:
                    visibility === "restricted"
                        ? Array.from(allowedIds).filter((id) => id !== doc.uploadedById)
                        : [],
            };
            await documentsApi.setVisibility(doc.id, payload);
            if (currentBandoId) await loadDocuments(currentBandoId);
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || err.message || "Errore nel salvataggio");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="rag-doc-visibility-modal__backdrop"
            role="dialog"
            aria-modal="true"
            onClick={onClose}
        >
            <div
                className="rag-doc-visibility-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="rag-doc-visibility-modal__header">
                    <h3 className="rag-doc-visibility-modal__title">
                        Visibilità di «{doc.filename}»
                    </h3>
                    <button
                        type="button"
                        className="rag-doc-visibility-modal__close"
                        onClick={onClose}
                        aria-label="Chiudi"
                    >
                        <X size={16} />
                    </button>
                </header>

                <div className="rag-doc-visibility-modal__body">
                    {loading ? (
                        <p className="rag-doc-visibility-modal__loading">Caricamento…</p>
                    ) : (
                        <>
                            <section className="rag-doc-visibility-modal__section">
                                <h4 className="rag-doc-visibility-modal__section-title">Modalità</h4>
                                <label className="rag-doc-visibility-modal__radio">
                                    <input
                                        type="radio"
                                        name="visibility"
                                        value="public"
                                        checked={visibility === "public"}
                                        onChange={() => setVisibility("public")}
                                    />
                                    <Globe size={14} />
                                    <span>
                                        <strong>Pubblico</strong>
                                        <small> — visibile a tutti i membri del bando</small>
                                    </span>
                                </label>
                                <label className="rag-doc-visibility-modal__radio">
                                    <input
                                        type="radio"
                                        name="visibility"
                                        value="restricted"
                                        checked={visibility === "restricted"}
                                        onChange={() => setVisibility("restricted")}
                                    />
                                    <Lock size={14} />
                                    <span>
                                        <strong>Ristretto</strong>
                                        <small> — visibile solo agli utenti selezionati</small>
                                    </span>
                                </label>
                            </section>

                            {visibility === "restricted" && (
                                <section className="rag-doc-visibility-modal__section">
                                    <h4 className="rag-doc-visibility-modal__section-title">
                                        Utenti autorizzati
                                    </h4>
                                    {bandoMembers.length === 0 ? (
                                        <p className="rag-doc-visibility-modal__empty">
                                            Nessun membro nel bando.
                                        </p>
                                    ) : (
                                        <ul className="rag-doc-visibility-modal__list">
                                            {bandoMembers.map((m) => {
                                                const isUploader = m.userId === doc.uploadedById;
                                                const checked = isUploader || allowedIds.has(m.userId);
                                                return (
                                                    <li
                                                        key={m.userId}
                                                        className="rag-doc-visibility-modal__item"
                                                    >
                                                        <label className="rag-doc-visibility-modal__checkbox">
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                disabled={isUploader}
                                                                onChange={() => toggleUser(m.userId)}
                                                            />
                                                            {m.role === "owner" ? (
                                                                <Crown
                                                                    size={14}
                                                                    className="rag-doc-visibility-modal__icon-owner"
                                                                />
                                                            ) : (
                                                                <User
                                                                    size={14}
                                                                    className="rag-doc-visibility-modal__icon-member"
                                                                />
                                                            )}
                                                            <span className="rag-doc-visibility-modal__username">
                                                                {m.username || `#${m.userId}`}
                                                                {isUploader && (
                                                                    <span className="rag-doc-visibility-modal__hint">
                                                                        {" "}
                                                                        (uploader, sempre incluso)
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </label>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </section>
                            )}

                            {error && (
                                <p className="rag-doc-visibility-modal__error">{error}</p>
                            )}
                        </>
                    )}
                </div>

                <footer className="rag-doc-visibility-modal__footer">
                    <button
                        type="button"
                        className="rag-doc-visibility-modal__btn"
                        onClick={onClose}
                        disabled={saving}
                    >
                        Annulla
                    </button>
                    <button
                        type="button"
                        className="rag-doc-visibility-modal__btn rag-doc-visibility-modal__btn--primary"
                        onClick={handleSave}
                        disabled={loading || saving}
                    >
                        {saving ? "Salvataggio…" : "Salva"}
                    </button>
                </footer>
            </div>
        </div>
    );
}
